import { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";

function normalizeColorForInput(css: string): string {
    const t = css.trim();
    if (/^#[\da-fA-F]{6}$/.test(t)) return t.toLowerCase();
    if (/^#[\da-fA-F]{3}$/.test(t)) {
        const r = t[1]!;
        const g = t[2]!;
        const b = t[3]!;
        return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
    }
    return "#888888";
}

export interface ColorPaletteEditorProps {
    colors: string[];
    onChange: (colors: string[]) => void;
    /** Shown under the control (e.g. dark → light hint). */
    description?: string;
    /** Minimum number of swatches (remove disabled at this count). */
    minColors?: number;
    /** Maximum number of swatches (add disabled at this count). */
    maxColors?: number;
    /** Initial value for the “next color” picker before Add. */
    defaultNewColor?: string;
    /** Extra class on the root. */
    className?: string;
}

/**
 * Editable list of CSS colors: per-row color input + remove, and an add row
 * (color picker + Add button).
 */
export function ColorPaletteEditor({
    colors,
    onChange,
    description,
    minColors = 0,
    maxColors,
    defaultNewColor = "#888888",
    className = "",
}: ColorPaletteEditorProps) {
    const [draftColor, setDraftColor] = useState(() =>
        normalizeColorForInput(defaultNewColor),
    );

    useEffect(() => {
        setDraftColor(normalizeColorForInput(defaultNewColor));
    }, [defaultNewColor]);

    const atMax = maxColors != null && colors.length >= maxColors;
    const canRemoveAny = colors.length > minColors;

    const updateAt = (index: number, hex: string) => {
        const next = [...colors];
        next[index] = hex;
        onChange(next);
    };

    const removeAt = (index: number) => {
        if (!canRemoveAny) return;
        onChange(colors.filter((_, j) => j !== index));
    };

    const addDraft = () => {
        if (atMax) return;
        onChange([...colors, draftColor]);
    };

    const rootClass = `color-palette-editor ${className}`.trim();

    return (
        <div className={rootClass}>
            {description != null && description !== "" && (
                <p
                    className="color-palette-editor__description path-tool-meta"
                    style={{ margin: "0 0 0.35rem" }}
                >
                    {description}
                </p>
            )}
            <ul
                className="color-palette-editor__list"
                aria-label="Color palette"
            >
                {/* Keys must stay stable while a row's color changes, or the native color picker closes. */}
                {colors.map((c, i) => (
                    <li key={i} className="color-palette-editor__row">
                        <input
                            type="color"
                            className="color-palette-editor__color-input"
                            value={normalizeColorForInput(c)}
                            onChange={(e) => updateAt(i, e.target.value)}
                            aria-label={`Color ${i + 1}`}
                            title={c}
                        />
                        <span
                            className="color-palette-editor__hex"
                            title={c}
                        >
                            {c}
                        </span>
                        <button
                            type="button"
                            className="generic-button color-palette-editor__remove"
                            disabled={!canRemoveAny}
                            onClick={() => removeAt(i)}
                            aria-label={`Remove color ${i + 1}`}
                        >
                            <X size={16} strokeWidth={2} aria-hidden />
                        </button>
                    </li>
                ))}
            </ul>
            <div className="color-palette-editor__add">
                <input
                    type="color"
                    className="color-palette-editor__color-input"
                    value={draftColor}
                    onChange={(e) => setDraftColor(e.target.value)}
                    aria-label="Color to add"
                />
                <button
                    type="button"
                    className="generic-button color-palette-editor__add-btn"
                    onClick={addDraft}
                    disabled={atMax}
                >
                    <Plus size={16} strokeWidth={2} aria-hidden />
                    Add color
                </button>
            </div>
        </div>
    );
}
